// src/hooks/useKeyboardShortcuts.js
import { useEffect, useCallback } from 'react';
import { useReactFlow } from 'reactflow';
import api from '../api';

/**
 * 键盘快捷键 + 拖放处理
 *
 * @param {object} deps
 * @param {Function} deps.saveProject
 * @param {Function} deps.undo
 * @param {Function} deps.redo
 * @param {Function} deps.fitView
 * @param {Function} deps.setNodes
 * @param {Function} deps.setAlertMsg
 * @param {object}   deps.clipboardRef
 * @param {Function} deps.handleDeleteSelectedNodes
 * @param {Function} deps.handleUpdateNode
 * @param {Function} deps.handleDeleteNodeButton
 * @param {Function} deps.handleGenerate
 * @param {Function} deps.handleDetachImage
 * @param {Function} deps.handleReuploadLogic
 * @param {Function} deps.handleReplace
 * @param {Function} deps.handleRemoveRefFile
 * @param {Function} deps.handleRemoveRefText
 * @param {Function} deps.handleRemoveRefAudio
 * @param {Function} deps.addNewNode
 * @param {Function} deps.processBatchUpload
 * @param {Function} deps.buildNodeData
 */
const useKeyboardShortcuts = (deps) => {
  const {
    saveProject, undo, redo, setNodes, setAlertMsg, clipboardRef,
    handleDeleteSelectedNodes,
    handleUpdateNode, handleDeleteNodeButton, handleGenerate, handleDetachImage,
    handleReuploadLogic, handleReplace, handleRemoveRefFile, handleRemoveRefText, handleRemoveRefAudio,
    addNewNode, processBatchUpload, buildNodeData,
  } = deps;

  const { getNodes, getEdges, setEdges, fitView, deleteElements, screenToFlowPosition } = useReactFlow();

  // --- 键盘快捷键 ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName) || document.activeElement.isContentEditable) return;

      const isCtrl = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();

      if (isCtrl && key === 's') { e.preventDefault(); saveProject(true); }
      if (key === 'z' && !isCtrl && !e.shiftKey) { fitView({ duration: 500, padding: 0.1 }); }
      if (isCtrl && key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); setAlertMsg("撤销 (Undo)"); }
      if (isCtrl && key === 'z' && e.shiftKey) { e.preventDefault(); redo(); setAlertMsg("重做 (Redo)"); }

      if (isCtrl && key === 'c') {
        // 有选中文本时（如剧本大师面板里选中的文字）交给浏览器原生复制，不抢去复制节点
        const sel = window.getSelection && window.getSelection();
        if (sel && !sel.isCollapsed && sel.toString().trim()) return;
        e.preventDefault();
        const selectedNodes = getNodes().filter(n => n.selected);
        if (selectedNodes.length > 0) {
          const selectedNodeIds = new Set(selectedNodes.map(n => n.id));
          const selectedEdges = getEdges().filter(
            e => selectedNodeIds.has(e.source) && selectedNodeIds.has(e.target)
          );
          clipboardRef.current = { nodes: selectedNodes, edges: selectedEdges };
          setAlertMsg(`已复制 ${selectedNodes.length} 个节点，${selectedEdges.length} 条连线`);
        }
      }

      if ((key === 'backspace' || key === 'delete') && !isCtrl) {
        e.preventDefault();
        handleDeleteSelectedNodes();
      }

      if (isCtrl && key === 'x') {
        // 同 Ctrl+C：有选中文本时让浏览器原生剪切，不抢去剪切节点
        const sel = window.getSelection && window.getSelection();
        if (sel && !sel.isCollapsed && sel.toString().trim()) return;
        e.preventDefault();
        const selectedNodes = getNodes().filter(n => n.selected);
        if (selectedNodes.length > 0) {
          const selectedNodeIds = new Set(selectedNodes.map(n => n.id));
          const selectedEdges = getEdges().filter(
            e => selectedNodeIds.has(e.source) && selectedNodeIds.has(e.target)
          );
          clipboardRef.current = { nodes: selectedNodes, edges: selectedEdges };
          handleDeleteSelectedNodes();
          setAlertMsg(`已剪切 ${selectedNodes.length} 个节点`);
        }
      }

      if (isCtrl && key === 'v') {
        e.preventDefault();
        // 兼容旧格式：clipboardRef 可能是数组（仅节点）或对象（节点+连线）
        const clipboardData = Array.isArray(clipboardRef.current)
          ? { nodes: clipboardRef.current, edges: [] }
          : (clipboardRef.current || { nodes: [], edges: [] });
        const nodesToPaste = clipboardData.nodes || [];
        const edgesToPaste = clipboardData.edges || [];
        if (nodesToPaste.length > 0) {
          setNodes(nds => nds.map(n => ({ ...n, selected: false })));
          const oldToNewId = new Map();
          const now = Date.now();

          const newNodes = nodesToPaste.map((node, idx) => {
            const newId = `node-${now}-${idx}-${Math.random().toString(36).substr(2, 6)}`;
            oldToNewId.set(node.id, newId);
            return {
              ...node, id: newId,
              position: { x: node.position.x + 50, y: node.position.y + 50 },
              selected: true,
              data: {
                ...node.data, id: newId,
                // 粘贴副本仅重置运行态，保留 output/outputs 内容，支持跨图板内容复制。
                loading: false, error: null,
                generationTime: null, generationStartTime: null, statusInfo: null,
                localCacheUrl: null,
                refFiles: (node.data?.refFiles || []).map(f => ({
                  ...f,
                  id: oldToNewId.get(f.id) || f.id,
                })),
                refTexts: (node.data?.refTexts || []).map(t => ({
                  ...t,
                  id: oldToNewId.get(t.id) || t.id,
                })),
                refAudios: (node.data?.refAudios || []).map(a => ({
                  ...a,
                  id: oldToNewId.get(a.id) || a.id,
                })),
                onUpdate: handleUpdateNode, onDelete: handleDeleteNodeButton,
                onGenerate: handleGenerate, onDetach: handleDetachImage,
                onReupload: handleReuploadLogic, onReplace: handleReplace,
                onRemoveRefFile: handleRemoveRefFile, onRemoveRefText: handleRemoveRefText,
                onRemoveRefAudio: handleRemoveRefAudio,
              }
            };
          });

          const newEdges = edgesToPaste
            .map((edge, idx) => {
              const newSource = oldToNewId.get(edge.source);
              const newTarget = oldToNewId.get(edge.target);
              if (!newSource || !newTarget) return null;
              return {
                ...edge,
                id: `edge-${newSource}-${newTarget}-${now}-${idx}`,
                source: newSource,
                target: newTarget,
                selected: false,
              };
            })
            .filter(Boolean);

          setNodes(nds => [...nds, ...newNodes]);
          if (newEdges.length > 0) {
            setEdges(eds => [...eds, ...newEdges]);
          }

          // 补充文件引用计数（带重试）
          (async () => {
            const allUrls = [];
            newNodes.forEach(n => {
              const d = n.data;
              const urls = [d.output, d.thumbnail, ...(d.outputs || []),
                ...(d.refFiles || []).map(f => f?.url),
                ...(d.refAudios || []).map(a => a?.url)
              ].filter(u => u && typeof u === 'string' && u.startsWith('http'));
              allUrls.push(...urls);
            });
            const results = await Promise.allSettled(allUrls.map(u => api.post('/api/files/register-ref', { url: u })));
            const failed = results.filter(r => r.status === 'rejected');
            if (failed.length > 0) {
              console.warn(`⚠️ ${failed.length}/${allUrls.length} 个文件引用注册失败，1秒后重试...`);
              await new Promise(r => setTimeout(r, 1000));
              await Promise.allSettled(failed.map((_, i) => {
                const failedUrl = allUrls[results.indexOf(failed[i])] || allUrls[i];
                return api.post('/api/files/register-ref', { url: failedUrl });
              }));
            }
          })();

          setAlertMsg(`已粘贴 ${newNodes.length} 个节点，${newEdges.length} 条连线`);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fitView, undo, redo, getNodes, getEdges, deleteElements, setNodes, setEdges, saveProject, handleDeleteSelectedNodes,
      handleUpdateNode, handleDeleteNodeButton, handleGenerate, handleDetachImage,
      handleReuploadLogic, handleReplace, handleRemoveRefFile, handleRemoveRefText, handleRemoveRefAudio,
      clipboardRef, setAlertMsg]);

  // --- 拖放 ---
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      processBatchUpload(files, position);
      return;
    }

    // 💬 从聊天窗口拖拽媒体到画布
    const cineartMedia = event.dataTransfer.getData('application/cineart-media');
    if (cineartMedia) {
      try {
        const data = JSON.parse(cineartMedia);
        const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
        const mediaType = data.type || 'image';
        const url = data.url;
        if (!url) return;
        const id = `node-${Date.now()}`;
        const customSize = mediaType === 'audio'
          ? { width: 160, height: 56 }
          : { width: 240, height: 240 };
        const newNode = {
          id, type: 'aiNode', position, selected: true,
          data: buildNodeData(id, mediaType, '聊天媒体', {
            output: url, outputs: [url], isDirectUpload: true, customSize,
          })
        };
        setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), newNode]);
      } catch (e) {
        console.error('解析聊天媒体数据失败:', e);
      }
      return;
    }

    const type = event.dataTransfer.getData('application/reactflow');
    if (!type) return;
    const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    addNewNode(type, position);
  }, [screenToFlowPosition, addNewNode, processBatchUpload, buildNodeData, setNodes]);

  return { onDragOver, onDrop };
};

export default useKeyboardShortcuts;
